import React, { useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import {
    AlertCircle,
    Check,
    CreditCard,
    FileText,
    IdCard,
    Phone,
    User,
    type LucideIcon,
} from "lucide-react";
import { cn } from "lib/utils";
import { ROUTES, buildRoute } from "routes";
import {
    buildProviderProfileCompletionSteps,
    PROFILE_COMPLETION_SECTION_ID,
} from "../profileCompletion";

export interface ProfileCompletionChecklistProps {
    pictureId?: string | null;
    identityVerified: boolean;
    bankDetailsComplete: boolean;
    hasDescription: boolean;
    hasPhone: boolean;
    dataleonLink?: string;
    hasDataleonQrcode?: boolean;
    stripeAccountId?: string;
    onOpenIdentityModal?: () => void;
    onOpenBankModal?: () => void;
}

interface ChecklistItemConfig {
    id: string;
    label: string;
    completed: boolean;
    icon: LucideIcon;
    href?: string;
    external?: boolean;
    onClick?: () => void;
    linkWhenCompleted?: boolean;
    pendingEmphasis?: boolean;
}

const ChecklistItem: React.FC<{ item: ChecklistItemConfig }> = ({ item }) => {
    const Icon = item.icon;
    const content = (
        <>
            {item.completed ? (
                <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-emerald-100">
                    <Check className="h-4 w-4 text-emerald-600" strokeWidth={3} />
                </span>
            ) : (
                <span
                    className={cn(
                        "flex h-8 w-8 shrink-0 items-center justify-center rounded-full",
                        item.pendingEmphasis ? "bg-red-50" : "bg-indigo-50"
                    )}
                >
                    <Icon
                        className={cn("h-4 w-4", item.pendingEmphasis ? "text-red-800/80" : "text-indigo-400")}
                        strokeWidth={2}
                    />
                </span>
            )}
            <span
                className={cn(
                    "text-sm font-medium",
                    item.completed ? "text-slate-400 line-through" : "text-gray-900"
                )}
            >
                {item.label}
            </span>
        </>
    );

    const className =
        "flex items-center gap-3 rounded-xl bg-white px-4 py-3.5 shadow-sm transition-colors";

    if ((!item.completed || item.linkWhenCompleted) && (item.href || item.onClick)) {
        if (item.onClick) {
            return (
                <button type="button" onClick={item.onClick} className={cn(className, "w-full text-left hover:bg-slate-50")}>
                    {content}
                </button>
            );
        }

        if (item.external) {
            return (
                <a
                    href={item.href}
                    target="_blank"
                    rel="noopener noreferrer"
                    className={cn(className, "hover:bg-slate-50")}
                >
                    {content}
                </a>
            );
        }

        return (
            <Link to={item.href!} className={cn(className, "hover:bg-slate-50")}>
                {content}
            </Link>
        );
    }

    return <div className={className}>{content}</div>;
};

const ProfileCompletionChecklist: React.FC<ProfileCompletionChecklistProps> = ({
    pictureId,
    identityVerified,
    bankDetailsComplete,
    hasDescription,
    hasPhone,
    dataleonLink,
    hasDataleonQrcode = false,
    stripeAccountId,
    onOpenIdentityModal,
    onOpenBankModal,
}) => {
    const { hash } = useLocation();

    useEffect(() => {
        if (hash !== `#${PROFILE_COMPLETION_SECTION_ID}`) return;

        const timer = window.setTimeout(() => {
            document
                .getElementById(PROFILE_COMPLETION_SECTION_ID)
                ?.scrollIntoView({ behavior: "smooth", block: "start" });
        }, 100);

        return () => window.clearTimeout(timer);
    }, [hash]);

    const stepIcons: Record<string, LucideIcon> = {
        photo: User,
        identity: IdCard,
        bank: CreditCard,
        description: FileText,
        phone: Phone,
    };

    const steps = buildProviderProfileCompletionSteps({
        pictureId,
        identityVerified,
        bankDetailsComplete,
        hasDescription,
        hasPhone,
    });

    const items: ChecklistItemConfig[] = steps.map((step) => {
        const base = {
            id: step.id,
            label: step.label,
            completed: step.completed,
            icon: stepIcons[step.id] ?? User,
        };

        if (step.id === "identity") {
            return {
                ...base,
                pendingEmphasis: !step.completed,
                onClick: hasDataleonQrcode ? onOpenIdentityModal : undefined,
                href: hasDataleonQrcode ? undefined : dataleonLink,
                external: !hasDataleonQrcode && Boolean(dataleonLink),
            };
        }

        if (step.id === "bank") {
            if (!stripeAccountId && onOpenBankModal) {
                return {
                    ...base,
                    onClick: onOpenBankModal,
                };
            }

            return {
                ...base,
                href: stripeAccountId ? buildRoute.stripeRedirect(stripeAccountId) : undefined,
                external: Boolean(stripeAccountId),
                linkWhenCompleted: Boolean(stripeAccountId),
            };
        }

        if (step.id === "phone") {
            return { ...base, href: ROUTES.ACCOUNT_PROFILE };
        }

        return base;
    });

    const allComplete = steps.every((step) => step.completed);

    if (allComplete) {
        return null;
    }

    const requiredComplete = steps.filter((step) => !step.optional).every((step) => step.completed);
    const title = requiredComplete
        ? "Vérifiez votre identité pour obtenir le badge vérifié"
        : "Finalisez votre profil pour commencer à proposer vos services";

    const identityReminder = requiredComplete;

    return (
        <section
            id={PROFILE_COMPLETION_SECTION_ID}
            className="relative scroll-mt-24 overflow-hidden rounded-3xl bg-[#f5f7ff] p-6"
        >
            <div
                className="pointer-events-none absolute -bottom-10 -right-10 h-40 w-40 rounded-full bg-indigo-100/40"
                aria-hidden
            />

            <div className="relative flex items-center gap-4">
                <span
                    className={cn(
                        "flex h-11 w-11 shrink-0 items-center justify-center rounded-xl shadow-sm",
                        identityReminder
                            ? "bg-red-50 text-red-800/80"
                            : "bg-primary text-primary-foreground"
                    )}
                >
                    {identityReminder ? (
                        <IdCard className="h-5 w-5" strokeWidth={2.25} />
                    ) : (
                        <AlertCircle className="h-5 w-5" strokeWidth={2.25} />
                    )}
                </span>
                <h2 className="text-base font-bold leading-snug text-gray-900 sm:text-lg">
                    {title}
                </h2>
            </div>

            <div className="mt-8 min-w-0 flex-1 space-y-4">
                <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
                    {items.map((item) => (
                        <ChecklistItem key={item.id} item={item} />
                    ))}
                </div>
            </div>
        </section>
    );
};

export default ProfileCompletionChecklist;
